'use client';

import clsx from 'clsx';
import { useEffect, useState } from 'react';
import {
    isMenuImageLoaded,
    markMenuImageLoaded,
    resolveMenuImageUrl,
} from './menu-image-cache';

const skeletonShimmer =
    'before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.8s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/8 before:to-transparent';

function SubMenuDetail({
    title,
    subtitle,
    imageUrl,
    imageDesc,
    isLoading = false,
    className,
}: Readonly<{
    title: string;
    subtitle: string;
    imageUrl: string | null;
    imageDesc: string | null;
    isLoading?: boolean;
    className?: string;
}>) {
    const resolvedImageUrl = resolveMenuImageUrl(imageUrl);
    const [imageLoaded, setImageLoaded] = useState(() =>
        isMenuImageLoaded(resolvedImageUrl),
    );

    useEffect(() => {
        setImageLoaded(isMenuImageLoaded(resolvedImageUrl));
    }, [resolvedImageUrl]);

    const showContentSkeleton = isLoading;
    const showImageSkeleton =
        showContentSkeleton || (!!resolvedImageUrl && !imageLoaded);
    const hasContent = Boolean(resolvedImageUrl || title || subtitle);

    if (!isLoading && !hasContent) {
        return <div className="inline-flex max-w-[400px] flex-1" />;
    }

    return (
        <div
            className={clsx(
                'inline-flex max-w-[400px] flex-col justify-between',
                className,
            )}
        >
            <div className="relative aspect-[200/119] h-[238px] self-start overflow-hidden rounded-[8px] bg-[#151518]">
                {showImageSkeleton && (
                    <div
                        aria-hidden="true"
                        className={clsx(
                            'absolute inset-0 overflow-hidden bg-[#1D1D22]',
                            skeletonShimmer,
                        )}
                    />
                )}
                {resolvedImageUrl && (
                    <img
                        alt={imageDesc ?? ''}
                        src={resolvedImageUrl}
                        width={400}
                        height={238}
                        decoding="async"
                        loading="eager"
                        draggable={false}
                        className={clsx(
                            'h-full w-full object-cover object-center transition-opacity duration-150',
                            imageLoaded ? 'opacity-100' : 'opacity-0',
                        )}
                        onLoad={() => {
                            markMenuImageLoaded(resolvedImageUrl);
                            setImageLoaded(true);
                        }}
                        onError={() => {
                            markMenuImageLoaded(resolvedImageUrl);
                            setImageLoaded(true);
                        }}
                    />
                )}
            </div>

            <div className="flex flex-col">
                {showContentSkeleton ? (
                    <div className="mt-[16px] flex flex-col gap-[10px]">
                        <div
                            aria-hidden="true"
                            className={clsx(
                                'relative h-[24px] w-[72%] overflow-hidden rounded-[6px] bg-[#1D1D22]',
                                skeletonShimmer,
                            )}
                        />
                        <div
                            aria-hidden="true"
                            className={clsx(
                                'relative h-[18px] w-[88%] overflow-hidden rounded-[6px] bg-[#1D1D22]',
                                skeletonShimmer,
                            )}
                        />
                    </div>
                ) : (
                    <>
                        <div
                            className={clsx(
                                'j-h6 mt-[16px] overflow-hidden text-ellipsis whitespace-nowrap text-left text-white',
                            )}
                        >
                            {title}
                        </div>
                        <div
                            className={clsx(
                                'j-t2 mt-[6px] overflow-hidden text-ellipsis whitespace-nowrap text-left text-white opacity-60',
                            )}
                        >
                            {subtitle}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default SubMenuDetail;
